/**
 * User Growth Page
 * Operational dashboard: registrations, activity, real-vs-team, platform, retention.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, UserPlus, Users, Repeat, Smartphone, Apple, HelpCircle } from 'lucide-react';
import { Card, Checkbox } from '@/components/ui';
import { Loading } from '@/components/common/Loading';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { useI18n } from '@/i18n';
import { parseBackendDateTime } from '@/lib/format/datetime';
import { userGrowthApi, type DailyGrowthRow } from '@/lib/api/userGrowth';

type TrendDays = 14 | 30;
type TableDays = 7 | 14 | 30;

function KpiCard({
  label,
  value,
  icon: Icon,
  color,
  bg,
}: {
  label: string;
  value: string;
  icon: typeof UserPlus;
  color: string;
  bg: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${bg}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

function classificationBadge(cls: string, labels: { admin: string; internalTester: string; regularUser: string }) {
  switch (cls) {
    case 'admin':
      return { text: labels.admin, cls: 'bg-purple-50 text-purple-700 border-purple-200' };
    case 'internal_tester':
      return { text: labels.internalTester, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    default:
      return { text: labels.regularUser, cls: 'bg-green-50 text-green-700 border-green-200' };
  }
}

function PlatformIcon({ platform }: { platform: string | null }) {
  if (platform === 'ios') return <Apple className="w-4 h-4 text-gray-600" />;
  if (platform === 'android') return <Smartphone className="w-4 h-4 text-green-600" />;
  return <HelpCircle className="w-4 h-4 text-gray-300" />;
}

function formatDateTime(ts: string | null): string {
  if (!ts) return '-';
  return parseBackendDateTime(ts).toLocaleDateString();
}

export function UserGrowthPage() {
  const { messages } = useI18n();
  const t = messages.userGrowth;

  const [trendDays, setTrendDays] = useState<TrendDays>(14);
  const [tableDays, setTableDays] = useState<TableDays>(7);
  const [excludeTeam, setExcludeTeam] = useState(true);

  const summaryQ = useQuery({
    queryKey: ['user-growth-summary'],
    queryFn: () => userGrowthApi.getSummary(),
  });

  const dailyQ = useQuery({
    queryKey: ['user-growth-daily', trendDays],
    queryFn: () => userGrowthApi.getDaily(trendDays),
  });

  const activeQ = useQuery({
    queryKey: ['user-growth-active', tableDays, excludeTeam],
    queryFn: () => userGrowthApi.getActiveUsers({ days: tableDays, exclude_team: excludeTeam }),
  });

  const funnelQ = useQuery({
    queryKey: ['user-growth-funnel'],
    queryFn: () => userGrowthApi.getFunnel(30),
  });

  const chartData = useMemo(
    () =>
      (dailyQ.data ?? []).map((r: DailyGrowthRow) => ({
        ...r,
        date: r.date.slice(5), // MM-DD
      })),
    [dailyQ.data],
  );

  const summary = summaryQ.data;
  const d1 = summary?.d1_retention_7d;

  return (
    <div className="p-6 space-y-6 max-w-[1400px]">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t.pageTitle}</h1>
        <p className="text-sm text-gray-500 mt-1">{t.pageDescription}</p>
      </div>

      {/* KPI cards */}
      {summaryQ.isLoading ? (
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 h-20 animate-pulse" />
          ))}
        </div>
      ) : summaryQ.isError ? (
        <ErrorMessage error={t.error} onRetry={() => summaryQ.refetch()} />
      ) : (
        <div className="grid grid-cols-4 gap-4">
          <KpiCard
            label={t.kpi.todayNewUsers}
            value={String(summary?.today_new_users ?? 0)}
            icon={UserPlus}
            color="text-blue-600"
            bg="bg-blue-50"
          />
          <KpiCard
            label={t.kpi.todayActive}
            value={`${summary?.today_active_regular ?? 0} / ${summary?.today_active_total ?? 0} ${t.kpi.todayActiveSuffix}`}
            icon={Users}
            color="text-green-600"
            bg="bg-green-50"
          />
          <KpiCard
            label={t.kpi.newUsers7d}
            value={String(summary?.new_users_7d ?? 0)}
            icon={TrendingUp}
            color="text-indigo-600"
            bg="bg-indigo-50"
          />
          <KpiCard
            label={t.kpi.d1Retention}
            value={d1 === null || d1 === undefined ? '—' : `${(d1 * 100).toFixed(1)}%`}
            icon={Repeat}
            color="text-amber-600"
            bg="bg-amber-50"
          />
        </div>
      )}

      {/* Trend chart */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700">{t.trend.title}</h3>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {([14, 30] as TrendDays[]).map((d) => (
              <button
                key={d}
                onClick={() => setTrendDays(d)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  trendDays === d ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {d === 14 ? t.trend.days14 : t.trend.days30}
              </button>
            ))}
          </div>
        </div>
        {dailyQ.isLoading ? (
          <div className="h-72 flex items-center justify-center">
            <Loading message={t.loading} />
          </div>
        ) : dailyQ.isError ? (
          <ErrorMessage error={t.error} onRetry={() => dailyQ.refetch()} />
        ) : !chartData.length ? (
          <div className="h-72 flex items-center justify-center text-gray-400">{t.table.empty}</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: 13,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="new_users_ios" stackId="new" name={t.trend.newUsersIos} fill="#3b82f6" />
              <Bar dataKey="new_users_android" stackId="new" name={t.trend.newUsersAndroid} fill="#22c55e" />
              <Bar dataKey="new_users_unknown" stackId="new" name={t.trend.newUsersUnknown} fill="#d1d5db" />
              <Line
                type="monotone"
                dataKey="active_regular"
                name={t.trend.activeRegular}
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="active_team"
                name={t.trend.activeTeam}
                stroke="#a855f7"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Daily active detail table */}
      <Card padding={false}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">{t.table.title}</h3>
          <div className="flex items-center gap-4">
            <Checkbox
              label={t.table.excludeTeam}
              checked={excludeTeam}
              onChange={(e) => setExcludeTeam(e.target.checked)}
            />
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {([7, 14, 30] as TableDays[]).map((d) => (
                <button
                  key={d}
                  onClick={() => setTableDays(d)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    tableDays === d ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {d === 7 ? t.table.days7 : d === 14 ? t.table.days14 : t.table.days30}
                </button>
              ))}
            </div>
          </div>
        </div>
        {activeQ.isLoading ? (
          <div className="p-8 flex justify-center">
            <Loading message={t.loading} />
          </div>
        ) : activeQ.isError ? (
          <div className="p-4">
            <ErrorMessage error={t.error} onRetry={() => activeQ.refetch()} />
          </div>
        ) : !activeQ.data?.length ? (
          <div className="p-8 text-center text-gray-400">{t.table.empty}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-2">{t.table.colDate}</th>
                  <th className="px-4 py-2">{t.table.colUser}</th>
                  <th className="px-4 py-2">{t.table.colClassification}</th>
                  <th className="px-4 py-2">{t.table.colRegistered}</th>
                  <th className="px-4 py-2 text-center">{t.table.colPlatform}</th>
                  <th className="px-4 py-2 text-right">{t.table.colSessions}</th>
                  <th className="px-4 py-2 text-right">{t.table.colRuns}</th>
                </tr>
              </thead>
              <tbody>
                {activeQ.data.map((row, idx) => {
                  const badge = classificationBadge(row.classification, t.classification);
                  return (
                    <tr key={`${row.date}-${row.user_id}-${idx}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{row.date}</td>
                      <td className="px-4 py-2.5">
                        <div className="truncate max-w-[180px] text-gray-900" title={row.user_id}>
                          {row.display_name}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-md border text-xs font-medium ${badge.cls}`}>
                          {badge.text}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">
                        <span className="inline-flex items-center gap-1.5">
                          {formatDateTime(row.registered_at)}
                          {row.is_new_user && (
                            <span className="inline-flex px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 text-[11px] font-medium">
                              {t.table.newBadge}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex justify-center">
                          <PlatformIcon platform={row.platform} />
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-700 font-mono">{row.sessions}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700 font-mono">{row.runs}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Funnel */}
      <Card>
        <h3 className="text-sm font-medium text-gray-700 mb-4">{t.funnel.title}</h3>
        {funnelQ.isLoading ? (
          <div className="h-24 flex items-center justify-center">
            <Loading message={t.loading} />
          </div>
        ) : funnelQ.isError ? (
          <ErrorMessage error={t.error} onRetry={() => funnelQ.refetch()} />
        ) : (
          <FunnelRow
            steps={[
              { label: t.funnel.registered, value: funnelQ.data?.registered ?? 0 },
              { label: t.funnel.createdSelfChart, value: funnelQ.data?.created_self_chart ?? 0 },
              { label: t.funnel.firstConversation, value: funnelQ.data?.first_conversation ?? 0 },
              { label: t.funnel.returned, value: funnelQ.data?.returned ?? 0 },
            ]}
          />
        )}
      </Card>
    </div>
  );
}

function FunnelRow({ steps }: { steps: { label: string; value: number }[] }) {
  const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500'];
  return (
    <div className="flex items-stretch gap-2">
      {steps.map((step, i) => {
        const prev = i > 0 ? steps[i - 1].value : null;
        const conv = prev && prev > 0 ? ((step.value / prev) * 100).toFixed(0) + '%' : null;
        return (
          <div key={step.label} className="flex items-center gap-2 flex-1">
            <div className={`flex-1 rounded-lg ${colors[i]} text-white p-4 text-center`}>
              <div className="text-2xl font-bold">{step.value}</div>
              <div className="text-xs opacity-90 mt-1">{step.label}</div>
            </div>
            {i < steps.length - 1 && (
              <div className="text-xs text-gray-400 whitespace-nowrap w-12 text-center">{conv ?? '→'}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
